import React, { useEffect, useState } from "react";
import { IoIosArrowRoundBack, IoIosArrowRoundForward } from "react-icons/io";
import { RotatingLines } from "react-loader-spinner";
import { useAuth } from "../../context/Auth";
import { useGlobalContext } from "../../context/stateContext";
import CreditDetails from "./CreditDetails";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { Link, useSearchParams } from "react-router-dom";
import ExportData from "../../components/ExportData";

const CustomerCreditList = () => {
  const [searchParams] = useSearchParams();
  const branch = searchParams.get("branch");
  const startDate = searchParams.get("start_date");
  const endDate = searchParams.get("end_date");
  const cashier = searchParams.get('cashier');
  const status = searchParams.get('status')
  const { activeSession } = useGlobalContext();
  const { requestWithAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [creditList, setCreditList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [removingItem, setRemovingItem] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [updating, setUpdating] = useState(false)
  const [tableData, setTableData] = useState([])

  const fetchCreditList = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/credit-sale?page=${current_page}&pageSize=${page_size}&search=${search_query}&credit_type=Protocol&branch=${branch}&startDate=${startDate}&endDate=${endDate}&cashier=${cashier}&status=${status}`,
        "GET"
      );
      setCreditList(data?.results);
      setNextPage(data?.next);
      setPrevPage(data?.prev);
      setTotalPages(Math.ceil(data?.count / pageSize));
    } catch (error) {
      console.error("Error fetching list:", error);
    } finally {
      setLoading(false);
    }
  };

  const getTableData = async (page_size, search_query, current_page) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/credit-sale?page=${current_page}&pageSize=10000000000000000000000000000000&search=${search_query}&credit_type=Protocol&branch=${branch}&startDate=${startDate}&endDate=${endDate}&cashier=${cashier}&status=${status}`,
        "GET"
      );
      setTableData(data?.results);
      
    } catch (error) {
      console.error("Error fetching list:", error);
    } finally {
      setLoading(false);
    }
  };

  const repayCredit = (credit) => {
    Swal.fire({
      icon:"question",
      title: `You are about to record full payment of GHs ${credit?.credit_cost} for this credit. Are you sure you want to proceed?`,
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      /* Read more about isConfirmed, isDenied below */
      if (result.isConfirmed) {
        setUpdating(true);
        const body = JSON.stringify({
            branch: activeSession?.branch?.id,
            session: activeSession?.session,
            sale:credit?.id,
            amount_paid:credit?.credit_cost
        })
        try {
          const data = await requestWithAuth(
            `/inventory/credit-payment/`,
            "POST",body, {
                "Content-Type": "application/json",
              }
          );
          Swal.fire({
            icon:"success",
            title: "Payment Recorded!",
            text: "Credit payment recorded successfully",
          }).then(() => {
            window.location.reload();
          });
        } catch (error) {
            if (error.response.data.error) {
                for (const property in error.response.data.error) {
                  toast.error(
                    `${property} error: ${error.response.data.error[property]}`
                  );
                }
              }
              if (error.response.status === 500) {
                toast.error("Something went wrong!");
              }
          
        } finally {
          setUpdating(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const filterCreditList = (search_query) => {
    setSearch(search_query);
    setCurrentPage(1); // Reset to page 1 whenever search query changes
  };

  const handleNextPage = () => {
    if (nextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (prevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3; // Number of pages to show around the current page
    const totalPagesToShow = 5; // Total pages to show at start and end

    let startPage = Math.max(1, currentPage - totalNumbersToShow);
    let endPage = Math.min(totalPages, currentPage + totalNumbersToShow);

    // Always show the first page
    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    // Show the numbers around the current page
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // Always show the last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number) => (
      <button
        key={number}
        onClick={() => number !== "..." && handlePageClick(number)}
        className={
          number === currentPage
            ? "border border-[#979797] bg-gradient-to-b from-white to-[#dcdcdc] py-[0.5rem] px-[1rem]"
            : "py-[0.5rem] px-[1rem]"
        }
      >
        {number}
      </button>
    ));
  };

  useEffect(() => {
    fetchCreditList(pageSize, search, currentPage);
    getTableData(pageSize, search, currentPage);
  }, [search, currentPage, pageSize]);

  return (
    <div className="p-[20px] border border-[#ddd]">
      {modalOpen && selectedCredit && (
        <CreditDetails
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          credit_sale={selectedCredit}
        />
      )}
      <div className="pt-[20px] flex justify-between mb-[20px]">
        <div className=" flex items-center gap-5">
          <ExportData tableData={tableData} fileName="Protocol Credit List" />
          <div className="flex items-center gap-2">
            <b>Show:</b>
            <select
              name=""
              id=""
              onChange={(e) => setPageSize(e.target.value)}
              className="border border-[#ddd] rounded-[3px] py-[7px] px-[12px] text-[13px]"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <b>Filter:</b>
          <input
            type="text"
            placeholder="Type to filter..."
            onChange={(e) => filterCreditList(e.target.value)}
            className="outline-none border border-[#ddd] rounded-[3px] h-[36px] w-full text-[#333] text-[13px] px-[12px] py-[7px]"
          />
        </div>
      </div>

      <div className="overflow-auto ">
        <table className="border-collapse w-full">
          <thead>
            <tr>
              <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                Phone Number
              </th>
              <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                Customer Name
              </th>
              <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                Credit Cost
              </th>
              <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                Quantity
              </th>
              <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                Credit Type
              </th>
              <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                Served By
              </th>
              <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                Created At
              </th>
              <th className="border-b border-t border-[#bbb] py-[12px] px-[20px]">
                Action
              </th>
            </tr>
          </thead>
          {!loading && (
            <tbody>
              {creditList?.map((item, index) => (
                <tr key={index} className="text-center">
                  <td className="text-center border-b border-[#bbb] py-[12px] px-[20px]">
                    {item?.phone_number}
                  </td>
                  <td className="text-center border-b border-[#bbb] py-[12px] px-[20px]">
                    {item?.customer}
                  </td>
                  <td className=" text-center border-b border-[#bbb] py-[12px] px-[20px]">
                    {item?.credit_cost}
                  </td>
                  <td className="text-center border-b border-[#bbb] py-[12px] px-[20px]">
                    {item?.quantity}
                  </td>
                  <td className="text-center border-b border-[#bbb] py-[12px] px-[20px]">
                    {item?.credit_type}
                  </td>
                  <td className="text-center border-b border-[#bbb] py-[12px] px-[20px]">
                    {item?.recorded_by}
                  </td>
                  <td className="text-center border-b border-[#bbb] py-[12px] px-[20px]">
                    {item?.created_at}
                  </td>

                  {activeSession?.user?.role !== "Cashier" ? (
                    <>
                      {updating ? (
                        <td className="border-b border-[#bbb] py-[12px] px-[20px] flex items-center justify-center gap-1">
                          <RotatingLines
                            height="20"
                            width="20"
                            strokeColor="black"
                          />{" "}
                          
                        </td>
                      ) : (
                        <td className="border-b border-[#bbb] py-[12px] px-[20px] flex items-center justify-center gap-1">
                          {
                            item?.payment_status !== "Paid" && (
                              <>
                                <Link
                                    to={`/transaction/repayment/credit/${item?.id}`}
                                  className="hover:bg-[#008fa1] bg-[#00BCD4] text-[10px] pt-[3px] px-[5px] pb-[1px] text-white cursor-pointer"
                                >
                                  VIEW DETAILS
                                </Link>
                                
                              </>
                            )
                          }
                          
                        </td>
                      )}
                    </>
                  ) : (
                    <>
                      {updating ? (
                        <td className="border-b border-[#bbb] py-[12px] px-[20px] flex items-center justify-center gap-1">
                          <RotatingLines
                            height="20"
                            width="20"
                            strokeColor="black"
                          />
                        </td>
                      ) : (
                        <td className="border-b border-[#bbb] py-[12px] px-[20px] flex items-center justify-center gap-1">
                          
                          <div
                            onClick={() => {
                              setSelectedCredit(item);
                              setModalOpen(true);
                            }}
                            className="hover:bg-[#008fa1] bg-[#00BCD4] text-[10px] pt-[3px] px-[5px] pb-[1px] text-white cursor-pointer"
                          >
                            VIEW DETAILS
                          </div>
                        </td>
                      )}
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {loading && (
          <div className="flex items-center justify-center py-10">
            <RotatingLines
              visible={true}
              height="30"
              width="30"
              color="black"
              strokeColor="black"
              strokeWidth="5"
              animationDuration="0.75"
              ariaLabel="rotating-lines-loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </div>
        )}
        {creditList?.length <= 0 && !loading && (
          <p className="text-center py-5">No data availabe in table</p>
        )}
        <div className="flex justify-between pt-[20px]">
          <p className="text-[12px] text-[#888]">
            Showing {currentPage} of {totalPages} pages
          </p>
          <div className="flex items-center gap-2">
            <IoIosArrowRoundBack
              className="text-[25px]"
              onClick={handleNextPage}
            />
            {renderPageNumbers()}
            <IoIosArrowRoundForward
              className="text-[25px]"
              onClick={handlePreviousPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerCreditList;
